import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Copy, Settings, AlertCircle, CheckCircle, Loader } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 4px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Content = styled.div`
  margin-bottom: 20px;
`;

const Message = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border-left: 4px solid ${props => 
    props.type === 'error' ? '#ef4444' : 
    props.type === 'success' ? '#10b981' : 
    props.type === 'warning' ? '#f59e0b' : '#3b82f6'
  };
  background: ${props => 
    props.type === 'error' ? '#fef2f2' : 
    props.type === 'success' ? '#f0fdf4' : 
    props.type === 'warning' ? '#fffbeb' : '#eff6ff'
  };
  color: ${props => 
    props.type === 'error' ? '#991b1b' : 
    props.type === 'success' ? '#065f46' : 
    props.type === 'warning' ? '#92400e' : '#1e40af'
  };
`;

const SelectableText = styled.div`
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
  user-select: text;
  cursor: text;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border: 1px solid #3b82f6;
    
    &:hover {
      background: #2563eb;
      border-color: #2563eb;
    }
  ` : props.variant === 'secondary' ? `
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
  ` : `
    background: transparent;
    color: #6b7280;
    border: 1px solid transparent;
    
    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressSection = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #3b82f6;
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress || 0}%;
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #6b7280;
  text-align: center;
`;

const TranscriptionDialog = ({ 
  isOpen, 
  onClose, 
  type = 'info', 
  title, 
  message, 
  selectableText,
  showProgress = false,
  progress = 0,
  progressText = '',
  onCopy,
  onOpenSettings,
  onRetry,
  actions = [],
  confirmAction = null
}) => {
  const [copyFeedback, setCopyFeedback] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || selectableText || message);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
      if (onCopy) onCopy();
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'error':
        return <AlertCircle size={20} />;
      case 'success':
        return <CheckCircle size={20} />;
      case 'loading':
        return <Loader size={20} className="animate-spin" />;
      default:
        return <AlertCircle size={20} />;
    }
  };

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Header>
          <Title>
            {getIcon()}
            {title || 'Transcription'}
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {message && (
            <Message type={type}>
              {message}
            </Message>
          )}

          {selectableText && (
            <SelectableText>
              {selectableText}
            </SelectableText>
          )}

          {showProgress && (
            <ProgressSection>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <ProgressText>{progressText}</ProgressText>
            </ProgressSection>
          )}
        </Content>

        <Actions>
          {selectableText && (
            <Button onClick={() => handleCopy(selectableText)}>
              {copyFeedback ? <CheckCircle size={14} /> : <Copy size={14} />}
              {copyFeedback ? 'Copied!' : 'Copy'}
            </Button>
          )}

          {onOpenSettings && (
            <Button variant="secondary" onClick={onOpenSettings}>
              <Settings size={14} />
              Settings
            </Button>
          )}

          {onRetry && (
            <Button variant="primary" onClick={onRetry}>
              Retry Transcription
            </Button>
          )}

          {confirmAction && (
            <Button 
              variant="primary" 
              onClick={confirmAction}
              style={{ 
                background: type === 'warning' ? '#dc3545' : '#3b82f6',
                borderColor: type === 'warning' ? '#dc3545' : '#3b82f6'
              }}
            >
              {type === 'warning' ? 'Yes, Clear' : 'Confirm'}
            </Button>
          )}

          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || 'secondary'}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.icon}
              {action.label}
            </Button>
          ))}

          <Button onClick={onClose}>
            {confirmAction ? 'Cancel' : 'Close'}
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default TranscriptionDialog; 